import { join } from "path";

import type { rest } from "@codesandbox/api";
import type { IPitcherClient } from "@codesandbox/pitcher-client";
import { router } from "router";

import type { EditorStoreProps } from "../types";

import { convertCRAToVite } from "./convert-cra";

export const runSandboxMigration = (props: EditorStoreProps) => {
  const sandboxId = router.queries.migrateFrom;
  const { api, pitcher } = props;

  api
    .get<{ data: rest.Sandbox }>({
      path: "/v1/sandboxes/" + sandboxId,
    })
    .then(async ({ data }) => {
      try {
        if (
          data.template === "create-react-app" ||
          data.template === "create-react-app-typescript"
        ) {
          const result = convertCRAToVite(data.modules, data.directories);

          // If it fails it could be because we default to "create-react-app"
          if (result.type === "ok") {
            data.modules = result.modules;
          }
        }

        await writeModules(pitcher, data, "");

        await pitcher.clients.task.createSetupTasks([
          {
            name: "Install Dependencies",
            command: "pnpm install",
          },
        ]);

        switch (data.template) {
          case "create-react-app-typescript":
          case "create-react-app": {
            await pitcher.clients.task.createTask({
              command: "pnpm dev",
              name: "Development",
              runAtStart: true,
              preview: {
                port: 5173,
              },
            });
            break;
          }
          case "static": {
            await pitcher.clients.task.createTask({
              command: "pnpm start",
              name: "Development",
              runAtStart: true,
              preview: {
                port: 5000,
              },
            });
            break;
          }
          case "parcel": {
            await pitcher.clients.task.createTask({
              command: "pnpm start",
              name: "Development",
              runAtStart: true,
              preview: {
                port: 1234,
              },
            });
          }
        }

        pitcher.clients.setup.onSetupProgressUpdate((setup) => {
          if (setup.state === "FINISHED") {
            router.setQuery("migrateFrom", undefined);
          }
        });

        pitcher.clients.setup.init();
      } catch (error) {
        props.addStatusNotification({
          status: "error",
          message: "Unable to migrate Sandbox, please report the issue",
        });
      }
    });
};

async function writeModules(
  pitcher: IPitcherClient,
  sandboxData: rest.Sandbox,
  writePath: string,
  currentDirShortid: string | null = null,
): Promise<void> {
  try {
    await pitcher.clients.fs.mkdir(writePath, true);
  } catch (e) {
    // eslint-disable-next-line
    console.warn("could not create directory: " + e);
  }

  const files = sandboxData.modules.filter(
    (m) => m.directoryShortid === currentDirShortid,
  );

  const directories = sandboxData.directories.filter(
    (d) => d.directoryShortid === currentDirShortid,
  );
  await Promise.all([
    ...files.map((file) => {
      const targetPath = join(writePath, file.title);

      if (file.isBinary) {
        return fetch(file.code)
          .then((x) => x.arrayBuffer())
          .then((content) =>
            pitcher.clients.fs.writeFile(
              targetPath,
              new Uint8Array(content),
              true,
              true,
            ),
          )
          .catch((e) => {
            // eslint-disable-next-line
            console.warn(
              `sandbox-fetcher: could not fetch file from ${file.code}: ${e.message}`,
            );

            const enc = new TextEncoder(); // always utf-8

            return pitcher.clients.fs.writeFile(
              targetPath,
              enc.encode(`Failed to fetch from: ${file.code}`),
              true,
              true,
            );
          });
      }

      const enc = new TextEncoder(); // always utf-8

      return pitcher.clients.fs.writeFile(
        targetPath,
        enc.encode(file.code),
        true,
        true,
      );
    }),
    ...directories.map((dir) =>
      writeModules(
        pitcher,
        sandboxData,
        join(writePath, dir.title),
        dir.shortid,
      ),
    ),
  ]);
}
